import React, { useEffect, useState, useRef } from 'react';
import QRCode from 'qrcode';

interface QRCodeDisplayProps {
  qrContent: string;
  timeoutSeconds?: number;
  timeoutAt?: string; // ISO expiry time, takes precedence when provided
  onTimeout?: () => void;
  title?: string;
  amount?: number;
  currency?: string; // e.g., 'THB', 'MYR' (overrides detection)
  transactionStatus?: string;
  errorMessage?: string;
}

const QRCodeDisplay: React.FC<QRCodeDisplayProps> = ({
  qrContent,
  timeoutSeconds = 60,
  timeoutAt,
  onTimeout,
  title = "QR Code",
  amount,
  currency,
  transactionStatus,
  errorMessage
}) => {
  const [qrDataUrl, setQrDataUrl] = useState<string>('');
  const [timeLeft, setTimeLeft] = useState<number>(timeoutSeconds);
  const [isExpired, setIsExpired] = useState<boolean>(false);
  const [isFailed, setIsFailed] = useState<boolean>(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Check if transaction has failed
  const isTransactionFailed = transactionStatus === 'failed' || transactionStatus === 'rejected';

  // Generate QR code
  useEffect(() => {
    const generateQR = async () => {
      try {
        const dataUrl = await QRCode.toDataURL(qrContent, {
          color: {
            dark: '#1F2937',  // Dark gray
            light: '#FFFFFF'  // White background
          },
          width: 256,
          margin: 2,
          errorCorrectionLevel: 'M'
        });
        setQrDataUrl(dataUrl);
      } catch (error) {
        console.error('Error generating QR code:', error);
      }
    };

    if (qrContent) {
      generateQR();
    }
  }, [qrContent]);

  // Handle transaction status changes
  useEffect(() => {
    if (isTransactionFailed) {
      setIsFailed(true);
      setIsExpired(true);
      // Clear the countdown timer
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
  }, [isTransactionFailed]);

  // Countdown timer (prioritize absolute timeoutAt when provided)
  useEffect(() => {
    if (isTransactionFailed) return;

    // Clear any existing timer
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Determine end time
    let endTimeMs: number | null = null;
    if (timeoutAt) {
      const at = new Date(timeoutAt);
      if (!isNaN(at.getTime())) {
        endTimeMs = at.getTime();
      }
    }
    if (!endTimeMs && typeof timeoutSeconds === 'number') {
      endTimeMs = Date.now() + Math.max(0, timeoutSeconds) * 1000;
    }
    if (!endTimeMs) return;

    setIsExpired(false);
    setIsFailed(false);

    const update = () => {
      const remaining = Math.max(0, Math.ceil((endTimeMs! - Date.now()) / 1000));
      setTimeLeft(remaining);
      if (remaining <= 0) {
        setIsExpired(true);
        if (onTimeout) onTimeout();
      }
    };

    // Initialize immediately and then every second
    update();
    intervalRef.current = setInterval(update, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [timeoutAt, timeoutSeconds, onTimeout, isTransactionFailed]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const detectCurrencyFromQR = (content: string): string | undefined => {
    try {
      const idx = content.indexOf('53'); // Tag 53 = Transaction Currency (ISO4217 numeric)
      if (idx >= 0 && idx + 4 <= content.length) {
        const len = parseInt(content.substring(idx + 2, idx + 4), 10);
        if (!isNaN(len) && idx + 4 + len <= content.length) {
          const val = content.substring(idx + 4, idx + 4 + len);
          if (val === '764') return 'THB';
          if (val === '458') return 'MYR';
          if (val === '702') return 'SGD';
        }
      }
    } catch {}
    return undefined;
  };

  const getLocaleForCurrency = (code: string): string => {
    switch (code) {
      case 'THB': return 'th-TH';
      case 'MYR': return 'en-MY';
      case 'SGD': return 'en-SG';
      case 'USD': return 'en-US';
      default: return 'en-US';
    }
  };

  const formatCurrency = (amount: number): string => {
    const provided = (currency || '').toUpperCase();
    const detected = detectCurrencyFromQR(qrContent);
    const code = provided || detected || 'MYR';
    return new Intl.NumberFormat(getLocaleForCurrency(code), {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 2
    }).format(amount);
  };

  const handleCopyQRContent = () => {
    navigator.clipboard.writeText(qrContent).then(() => {
      // Could add a toast notification here
      console.log('QR content copied to clipboard');
    }).catch(() => {
      console.error('Failed to copy QR content');
    });
  };

  const handleDownloadQR = () => {
    if (qrDataUrl) {
      const link = document.createElement('a');
      link.href = qrDataUrl;
      link.download = `qr-payment-${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <div className="border-2 border-blue-200 rounded-lg p-6 bg-white text-center max-w-sm mx-auto shadow-lg">
      <h3 className="text-lg font-semibold text-blue-800 mb-4">
        {title}
      </h3>

      {amount && (
        <div className="mb-4">
          <div className="text-2xl font-bold text-gray-900">
            {formatCurrency(amount)}
          </div>
        </div>
      )}

      {qrDataUrl && (
        <div className="mb-4">
          <img 
            src={qrDataUrl} 
            alt="QR Code" 
            className={`max-w-full h-auto border-2 rounded-lg mx-auto ${
              isFailed ? 'opacity-50 grayscale border-red-300' : isExpired ? 'opacity-50 grayscale border-gray-300' : 'border-blue-200'
            }`}
            style={{ maxWidth: '200px' }}
          />
        </div>
      )}

      {/* Countdown Timer / Status */}
      {(timeoutAt || (timeoutSeconds && timeoutSeconds > 0)) && (
        <div className="mb-4">
          <div className={`text-lg font-mono ${
            isFailed ? 'text-red-600' : isExpired ? 'text-red-600' : timeLeft <= 10 ? 'text-orange-600' : 'text-green-600'
          }`}>
            {isFailed ? 'FAILED' : isExpired ? 'EXPIRED' : formatTime(timeLeft)}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {isFailed
              ? (errorMessage || 'Transaction failed')
              : isExpired
              ? 'This QR code has expired'
              : `QR code expires in ${formatTime(timeLeft)}`
            }
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="space-y-2">
        <button
          onClick={handleDownloadQR}
          disabled={!qrDataUrl || isExpired || isFailed}
          className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white py-2 px-4 rounded-md text-sm font-medium"
        >
          📥 Download QR Code
        </button>
        <button
          onClick={handleCopyQRContent}
          disabled={!qrContent || isExpired || isFailed}
          className="w-full bg-gray-600 hover:bg-gray-700 disabled:bg-gray-400 text-white py-2 px-4 rounded-md text-sm font-medium"
        >
          📋 Copy QR Content
        </button>
      </div>

      {/* QR Content Preview */}
      <div className="mt-4 text-xs text-gray-500 bg-gray-50 p-2 rounded border">
        <div className="font-medium mb-1">QR Content:</div>
        <div className="break-all font-mono">
          {qrContent.length > 80 ? `${qrContent.substring(0, 80)}...` : qrContent}
        </div>
      </div>

      {isExpired && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <div className="text-red-800 text-sm font-medium">
            ⚠️ QR Code Expired
          </div>
          <div className="text-red-600 text-xs mt-1">
            This payment request has timed out. Please create a new payment.
          </div>
        </div>
      )}
    </div>
  );
};

export default QRCodeDisplay;
